/**
 * The notation itself.
 *
 * Drawn as inline SVG rather than on a canvas because genogram notation *is*
 * line-work — double lines, zigzags, slashes, dotted enclosures — which reads
 * badly rastered at zoom, and because SVG gives hit-testing, a focus ring and a
 * text layer for free. That is also what keeps the board keyboard-operable and
 * legible to a screen reader.
 *
 * Every symbol below follows a published convention (McGoldrick/Gerson for the
 * genogram, Hartman for the ecomap) and is named after it. Changing one changes
 * what a clinician reads, so change it deliberately.
 *
 * Colour comes from `currentColor` throughout, so a selected or attributed
 * element re-tints by swapping a text class rather than by threading hex values
 * through the tree.
 */

import {
    NODE_RADIUS,
    handleAlong,
    NOTE_LINE_HEIGHT,
    NOTE_PADDING,
    NOTE_WIDTH,
    barMarks,
    noteHeight,
    wrapNote,
    sibshipGeometry,
    offsetSegment,
    parallelOffsets,
    slashMarks,
    toPath,
    unionGeometry,
    zigzagPoints,
    type Point,
} from "../../domain/layout";
import { DEFAULT_CENTRE_RADIUS, DEFAULT_SYSTEM_RADIUS } from "../../domain/types";
import type {
    ChildType,
    NoteNode,
    EmotionalType,
    LinkStrength,
    PersonNode,
    SystemNode,
    UnionType,
} from "../../domain/types";

const R = NODE_RADIUS;

// --- People ---------------------------------------------------------------

/**
 * The person's outline: square male, circle female, diamond unknown, triangle
 * pregnancy. The shape is the sex, and nothing else on the board uses it.
 */
export function PersonShape({
    person,
    filled = false,
}: {
    person: PersonNode;
    /**
     * Painted solid. A pregnancy lost early is drawn as a filled figure — the
     * convention's way of saying a person was there and did not arrive.
     */
    filled?: boolean;
}) {
    const common = {
        // `currentColor`, not black: the board is read in a dark theme too,
        // where black would be a hole rather than a mark.
        fill: filled ? "currentColor" : "var(--color-surface-raised, #fff)",
        stroke: "currentColor",
        strokeWidth: person.index ? 2 : 1.75,
    };

    switch (person.gender) {
        case "female":
            return <circle cx={0} cy={0} r={R} {...common} />;
        case "unknown":
            return (
                <polygon
                    points={`0,${-R} ${R},0 0,${R} ${-R},0`}
                    {...common}
                />
            );
        case "pregnancy":
            return (
                <polygon points={`0,${-R} ${R},${R} ${-R},${R}`} {...common} />
            );
        default:
            return <rect x={-R} y={-R} width={R * 2} height={R * 2} {...common} />;
    }
}

/** The double outline that marks the person the family is being read around. */
export function IndexRing({ person }: { person: PersonNode }) {
    if (!person.index) return null;
    const outer = R + 5;
    const common = { fill: "none", stroke: "currentColor", strokeWidth: 1.5 };

    switch (person.gender) {
        case "female":
            return <circle cx={0} cy={0} r={outer} {...common} />;
        case "unknown":
            return <polygon points={`0,${-outer} ${outer},0 0,${outer} ${-outer},0`} {...common} />;
        case "pregnancy":
            return <polygon points={`0,${-outer} ${outer},${outer} ${-outer},${outer}`} {...common} />;
        default:
            return (
                <rect
                    x={-outer}
                    y={-outer}
                    width={outer * 2}
                    height={outer * 2}
                    {...common}
                />
            );
    }
}

/** The cross through the shape. A death year implies it; the flag alone also does. */
export function DeceasedCross({ person }: { person: PersonNode }) {
    if (!person.deceased) return null;
    const arm = R + 4;
    return (
        <g stroke="currentColor" strokeWidth={1.75} strokeLinecap="round">
            <line x1={-arm} y1={-arm} x2={arm} y2={arm} />
            <line x1={arm} y1={-arm} x2={-arm} y2={arm} />
        </g>
    );
}

// --- Ecomap nodes ---------------------------------------------------------

/**
 * The ring the household is drawn inside.
 *
 * Hartman's ecomap is one circle with the family in it and the systems around
 * it — not a dot with everyone beside it. The ring is wide, faint and behind
 * everything: it is a boundary, and a boundary that competes with the figures
 * inside it is a badly drawn one.
 */
export function CentreShape({
    radius = DEFAULT_CENTRE_RADIUS,
    label = "",
}: {
    radius?: number;
    label?: string;
}) {
    return (
        <g>
            <circle
                cx={0}
                cy={0}
                r={radius}
                fill="var(--color-accent-tint, #f3f0fe)"
                fillOpacity={0.35}
                stroke="currentColor"
                strokeWidth={2}
            />
            {label && (
                <text
                    x={0}
                    y={-radius + 22}
                    textAnchor="middle"
                    fill="currentColor"
                    fontSize={14}
                    fontWeight={600}
                >
                    {label}
                </text>
            )}
        </g>
    );
}

export function SystemShape({
    system,
    radius,
}: {
    system: SystemNode;
    /** Overrides the stored size while it is being dragged. */
    radius?: number;
}) {
    // A custom system is whatever the patient named it, so it is drawn as an
    // open shape rather than borrowing another category's mark.
    const dashed = system.system === "custom";
    return (
        <circle
            cx={0}
            cy={0}
            r={radius ?? system.radius ?? DEFAULT_SYSTEM_RADIUS}
            fill="var(--color-surface-raised, #fff)"
            stroke="currentColor"
            strokeWidth={1.75}
            strokeDasharray={dashed ? "5 4" : undefined}
        />
    );
}

/**
 * A note pinned to the board, and whose it is.
 *
 * The name is on the card rather than in a tooltip: in a session the useful
 * question is "who said that", asked while looking at the board, and an
 * answer that needs a hover is no answer at all.
 *
 * Wrapped by hand into `tspan`s rather than laid out by a `foreignObject`,
 * because the board is rasterised into a PNG for the record and a foreign
 * object does not survive that trip.
 */
export function NoteCard({ note }: { note: NoteNode }) {
    const lines = wrapNote(note.text);
    const author = note.author?.trim() || "";
    const height = noteHeight(lines.length, Boolean(author));
    const left = -NOTE_WIDTH / 2;
    const top = -height / 2;

    return (
        <g>
            <rect
                x={left}
                y={top}
                width={NOTE_WIDTH}
                height={height}
                rx={10}
                fill="var(--color-surface-raised, #fff)"
                stroke="currentColor"
                strokeWidth={1.5}
            />
            <g fill="currentColor">
                {lines.map((line, index) => (
                    <text
                        key={index}
                        x={left + NOTE_PADDING}
                        y={top + NOTE_PADDING + NOTE_LINE_HEIGHT * (index + 0.8)}
                        fontSize={13}
                    >
                        {line}
                    </text>
                ))}
                {author && (
                    <>
                        {/* A rule under the words: without it the name reads
                            as one more line of the note. */}
                        <line
                            x1={left + NOTE_PADDING}
                            x2={-left - NOTE_PADDING}
                            y1={top + height - NOTE_LINE_HEIGHT - 6}
                            y2={top + height - NOTE_LINE_HEIGHT - 6}
                            stroke="currentColor"
                            strokeWidth={1}
                            opacity={0.25}
                        />
                        <text
                            x={left + NOTE_PADDING}
                            y={top + height - NOTE_PADDING}
                            fontSize={12}
                            fontWeight={600}
                            opacity={0.7}
                        >
                            {author}
                        </text>
                    </>
                )}
            </g>
        </g>
    );
}

/** The outline that shows a note is picked, drawn around its own card. */
export function NoteOutline({ note }: { note: NoteNode }) {
    const height = noteHeight(wrapNote(note.text).length, Boolean(note.author?.trim()));
    return (
        <rect
            x={-NOTE_WIDTH / 2 - 6}
            y={-height / 2 - 6}
            width={NOTE_WIDTH + 12}
            height={height + 12}
            rx={14}
            fill="none"
            stroke="currentColor"
            strokeWidth={2}
        />
    );
}

// --- Links ----------------------------------------------------------------

/** How many slashes cross a union line, by what happened to it. */
const UNION_SLASHES: Partial<Record<UnionType, number>> = {
    separated: 1,
    divorced: 2,
    annulled: 2,
    ended: 1,
};

const DASHED_UNIONS: UnionType[] = ["cohabiting", "engaged"];

export function UnionLine({
    a,
    b,
    union,
    since,
    until,
}: {
    a: Point;
    b: Point;
    union: UnionType;
    since?: number | null;
    until?: number | null;
}) {
    const geometry = unionGeometry(a, b);
    const slashes = UNION_SLASHES[union] ?? 0;
    const [runStart, runEnd] = [geometry.points[1], geometry.points[2]];
    // "1984–2010" beside the couple's line, which is where a genogram carries
    // it: when a family formed and, if it ended, when.
    const dates = [since, until].some(Boolean)
        ? `${since ?? ""}–${until ?? ""}`.replace(/^–|–$/, "")
        : null;

    return (
        <g stroke="currentColor" strokeWidth={1.75} fill="none">
            <path
                d={toPath(geometry.points)}
                strokeDasharray={DASHED_UNIONS.includes(union) ? "6 4" : undefined}
            />
            {slashMarks(runStart, runEnd, slashes).map(([start, end], index) => (
                <line
                    key={index}
                    x1={start.x}
                    y1={start.y}
                    x2={end.x}
                    y2={end.y}
                    strokeWidth={2}
                />
            ))}
            {dates && (
                <text
                    x={geometry.anchor.x}
                    y={geometry.anchor.y - 7}
                    textAnchor="middle"
                    stroke="none"
                    fill="currentColor"
                    fontSize={12}
                    fontWeight={600}
                >
                    {dates}
                </text>
            )}
        </g>
    );
}

/**
 * Filiation, drawn the way the convention draws it.
 *
 * Natural is a solid line, adopted a dotted one; twins meet at a point above
 * the pair, and identical twins carry a bar between them — that bar is the
 * whole difference between mellizos and gemelos. A pregnancy that did not end
 * in a birth is marked on the figure itself: filled solid for a miscarriage,
 * crossed through for a termination or a stillbirth.
 */
const DOTTED_CHILDREN: ChildType[] = ["adopted", "foster", "step"];
const CROSSED_LOSSES: ChildType[] = ["stillbirth", "termination"];

export const twinKindOf = (kind: ChildType): "none" | "fraternal" | "identical" =>
    kind === "identicalTwin" ? "identical" : kind === "twin" ? "fraternal" : "none";

export interface SibshipMember {
    id: string;
    kind: ChildType;
    at: Point;
    selected: boolean;
    /** The therapist's own colour for this child's line, if they set one. */
    ink?: string;
    label: string;
    onSelect: () => void;
}

/** One couple's children: the line they hang from, and each child's own. */
export function Sibship({
    anchor,
    members,
}: {
    anchor: Point;
    members: SibshipMember[];
}) {
    const geometry = sibshipGeometry(
        anchor,
        members.map((member) => ({
            id: member.id,
            at: member.at,
            twin: twinKindOf(member.kind),
        })),
    );
    if (!geometry) return null;

    const byId = new Map(members.map((member) => [member.id, member]));

    return (
        <g fill="none">
            <g stroke="currentColor" strokeWidth={1.75} className="text-content-secondary">
                <line
                    x1={geometry.trunk[0].x}
                    y1={geometry.trunk[0].y}
                    x2={geometry.trunk[1].x}
                    y2={geometry.trunk[1].y}
                />
                <line
                    x1={geometry.line[0].x}
                    y1={geometry.line[0].y}
                    x2={geometry.line[1].x}
                    y2={geometry.line[1].y}
                />
                {geometry.identicalBars.map(([left, right], index) => (
                    <line
                        key={`bar-${index}`}
                        x1={left.x}
                        y1={left.y}
                        x2={right.x}
                        y2={right.y}
                    />
                ))}
            </g>

            {geometry.drops.map((drop) => {
                const member = byId.get(drop.id);
                if (!member) return null;
                const loss = CROSSED_LOSSES.includes(member.kind);
                const end = loss
                    ? { x: drop.to.x, y: drop.to.y - R * 0.6 }
                    : drop.to;

                const handle = handleAlong(drop.from, end, NODE_RADIUS + 8);

                return (
                    <g
                        key={drop.id}
                        style={member.selected ? undefined : { color: member.ink }}
                        className={`cursor-pointer outline-none ${
                            member.selected ? "text-accent" : "text-content-secondary"
                        }`}
                    >
                        <line
                            x1={drop.from.x}
                            y1={drop.from.y}
                            x2={end.x}
                            y2={end.y}
                            stroke="currentColor"
                            strokeWidth={1.75}
                            strokeDasharray={
                                DOTTED_CHILDREN.includes(member.kind) ? "2 4" : undefined
                            }
                        />

                        {(member.kind === "termination" || member.kind === "stillbirth") && (
                            <g stroke="currentColor" strokeWidth={2.5} strokeLinecap="round">
                                <line x1={end.x - 8} y1={end.y} x2={end.x + 8} y2={end.y + 16} />
                                <line x1={end.x + 8} y1={end.y} x2={end.x - 8} y2={end.y + 16} />
                            </g>
                        )}

                        {/* The grab point, on the child's own line. */}
                        <circle
                            cx={handle.x}
                            cy={handle.y}
                            r={14}
                            fill="transparent"
                            tabIndex={0}
                            role="button"
                            aria-label={member.label}
                            data-testid={`link-${member.id}`}
                            onClick={member.onSelect}
                            onKeyDown={(event) => {
                                if (event.key === "Enter" || event.key === " ") {
                                    event.preventDefault();
                                    member.onSelect();
                                }
                            }}
                        />
                        {member.selected && (
                            <circle cx={handle.x} cy={handle.y} r={5} className="fill-current" />
                        )}
                    </g>
                );
            })}
        </g>
    );
}

/** How many lines an emotional relationship is drawn with, and whether it zigzags. */
const EMOTIONAL_SHAPE: Record<
    EmotionalType,
    { lines: number; dashed?: boolean; conflict?: boolean; cuts?: number; bars?: number }
> = {
    close: { lines: 2 },
    fused: { lines: 3 },
    distant: { lines: 1, dashed: true },
    hostile: { lines: 1, conflict: true },
    cutoff: { lines: 1, cuts: 2 },
    // "Quiebre": the line barred through, upright, rather than slashed.
    broken: { lines: 1, bars: 2 },
    fusedHostile: { lines: 3, conflict: true },
    closeHostile: { lines: 2, conflict: true },
    neglect: { lines: 1, dashed: true, cuts: 1 },
    abuse: { lines: 2, conflict: true, cuts: 1 },
};

/**
 * What an unknown symbol is drawn as.
 *
 * Two people draw on one board and one of them can be a version behind — a
 * tab left open across a deploy is enough. A symbol this build has never heard
 * of is drawn as a plain line rather than crashing the board out from under
 * whoever is holding the older copy.
 */
const PLAIN_LINE = { lines: 1 } as const;

export function EmotionalLine({
    a,
    b,
    kind,
}: {
    a: Point;
    b: Point;
    kind: EmotionalType;
}) {
    const shape = EMOTIONAL_SHAPE[kind] ?? PLAIN_LINE;

    return (
        <g stroke="currentColor" strokeWidth={1.5} fill="none">
            {parallelOffsets(shape.lines).map((offset, index) => {
                const [start, end] = offsetSegment(a, b, offset);
                return (
                    <line
                        key={index}
                        x1={start.x}
                        y1={start.y}
                        x2={end.x}
                        y2={end.y}
                        strokeDasharray={shape.dashed ? "6 5" : undefined}
                    />
                );
            })}
            {shape.conflict && (
                <path d={toPath(zigzagPoints(a, b))} strokeWidth={1.75} />
            )}
            {shape.bars
                ? barMarks(a, b, shape.bars).map(([start, end], index) => (
                      <line
                          key={`bar-${index}`}
                          x1={start.x}
                          y1={start.y}
                          x2={end.x}
                          y2={end.y}
                          strokeWidth={2.5}
                      />
                  ))
                : null}
            {shape.cuts
                ? slashMarks(a, b, shape.cuts).map(([start, end], index) => (
                      <line
                          key={`cut-${index}`}
                          x1={start.x}
                          y1={start.y}
                          x2={end.x}
                          y2={end.y}
                          strokeWidth={2}
                      />
                  ))
                : null}
        </g>
    );
}

/** Stroke weight and pattern carry the strength of an ecomap tie. */
const STRENGTH_STROKE: Record<LinkStrength, { width: number; dash?: string }> = {
    strong: { width: 3 },
    tenuous: { width: 1.25, dash: "3 5" },
    stressful: { width: 1.75 },
};

export function SystemTie({
    a,
    b,
    strength,
    arrowAtA,
    arrowAtB,
    markerStart,
    markerEnd,
}: {
    a: Point;
    b: Point;
    strength: LinkStrength;
    /**
     * Which ends carry an arrowhead.
     *
     * Told rather than worked out from the energy, because "toward the person"
     * is an end of the *tie*, not an end of the stored link: the therapist may
     * have picked the system first.
     */
    arrowAtA: boolean;
    arrowAtB: boolean;
    markerStart: string;
    markerEnd: string;
}) {
    const stroke = STRENGTH_STROKE[strength] ?? STRENGTH_STROKE.stressful;
    // Stress is drawn as a hatched line in Hartman's notation — the same
    // "this costs something" reading as the genogram's conflict zigzag.
    const path = strength === "stressful" ? toPath(zigzagPoints(a, b, 4, 12)) : undefined;

    return (
        <g stroke="currentColor" fill="none" strokeWidth={stroke.width}>
            {path ? (
                <path
                    d={path}
                    markerStart={arrowAtA ? `url(#${markerStart})` : undefined}
                    markerEnd={arrowAtB ? `url(#${markerEnd})` : undefined}
                />
            ) : (
                <line
                    x1={a.x}
                    y1={a.y}
                    x2={b.x}
                    y2={b.y}
                    strokeDasharray={stroke.dash}
                    markerStart={arrowAtA ? `url(#${markerStart})` : undefined}
                    markerEnd={arrowAtB ? `url(#${markerEnd})` : undefined}
                />
            )}
        </g>
    );
}

/**
 * The arrowheads energy direction is drawn with.
 *
 * Defined once per board and referenced by id: a marker per link would put
 * hundreds of identical defs in the document.
 */
export function ArrowMarkers({ idPrefix }: { idPrefix: string }) {
    return (
        <defs>
            <marker
                id={`${idPrefix}-start`}
                viewBox="0 0 10 10"
                refX="9"
                refY="5"
                markerWidth="7"
                markerHeight="7"
                orient="auto-start-reverse"
            >
                <path d="M 0 0 L 10 5 L 0 10 z" fill="currentColor" />
            </marker>
            <marker
                id={`${idPrefix}-end`}
                viewBox="0 0 10 10"
                refX="9"
                refY="5"
                markerWidth="7"
                markerHeight="7"
                orient="auto"
            >
                <path d="M 0 0 L 10 5 L 0 10 z" fill="currentColor" />
            </marker>
        </defs>
    );
}

/** The dotted enclosure around everyone currently living together. */
export function HouseholdBoundary({
    points,
    label,
}: {
    points: Point[];
    label: string;
}) {
    if (points.length === 0) return null;
    const padding = R + 18;
    const minX = Math.min(...points.map((p) => p.x)) - padding;
    const minY = Math.min(...points.map((p) => p.y)) - padding;
    const maxX = Math.max(...points.map((p) => p.x)) + padding;
    const maxY = Math.max(...points.map((p) => p.y)) + padding;

    return (
        <g>
            <rect
                x={minX}
                y={minY}
                width={maxX - minX}
                height={maxY - minY}
                rx={20}
                fill="none"
                stroke="currentColor"
                strokeWidth={1.25}
                strokeDasharray="2 6"
            />
            {label && (
                <text
                    x={minX + 12}
                    y={minY - 6}
                    fill="currentColor"
                    fontSize={12}
                    fontWeight={600}
                >
                    {label}
                </text>
            )}
        </g>
    );
}
